import { useCallback, useRef, useState } from 'react';

// Cues are synthesised rather than sampled: no audio files to load, and every
// sound is tunable in one place. The AudioContext is created lazily because
// browsers refuse to start one before a user gesture.
const CUES = {
  tap: { freq: 430, dur: 0.045, type: 'triangle', gain: 0.035 },
  forward: { freq: 380, to: 560, dur: 0.085, type: 'triangle' },
  back: { freq: 560, to: 380, dur: 0.085, type: 'triangle' },
  open: { freq: 300, to: 720, dur: 0.16 },
  close: { freq: 640, to: 260, dur: 0.14 },
  release: { freq: 300, dur: 0.05, gain: 0.025 },
  error: { freq: 150, to: 110, dur: 0.16, type: 'square', gain: 0.03 },
};

export function useToolSound() {
  const ctxRef = useRef(null);
  const [muted, setMuted] = useState(false);

  const play = useCallback(
    name => {
      if (muted || typeof window === 'undefined') return;
      const cue = CUES[name];
      if (!cue) return;

      let ctx = ctxRef.current;
      if (!ctx) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return;
        ctx = ctxRef.current = new Ctor();
      }
      if (ctx.state === 'suspended') ctx.resume();

      const { freq, to, dur, type = 'sine', gain = 0.05 } = cue;
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      if (to) osc.frequency.exponentialRampToValueAtTime(to, ctx.currentTime + dur);
      amp.gain.setValueAtTime(0.0001, ctx.currentTime);
      amp.gain.linearRampToValueAtTime(gain, ctx.currentTime + 0.006);
      amp.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
      osc.connect(amp).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + dur + 0.02);
    },
    [muted]
  );

  return { play, muted, setMuted };
}
